import { MarginInfo } from "./margin-info";
import { TradingPanelCard } from "@/features/shared/trading-panel-card";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { useState } from "react";
import { UserIcon } from "lucide-react";
import { PortfolioValueCard } from "@/features/account/components/portfolio-value-card";
import { AccountHealthCard } from "@/features/account/components/account-health-card";
import { PositionDistributionCard } from "@/features/account/components/position-distribution-card";
import { TradingStatsCard } from "@/features/account/components/trading-stats-card";
import { RecentActivityCard } from "@/features/account/components/recent-activity-card";
import { Button } from "@/components/ui/button";

type AccountPanelProps = {
  panelId: string;
};

export const AccountPanel = ({ panelId: _panelId }: AccountPanelProps) => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const { isAuthenticated } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // On mobile, show full account content
  if (isMobile) {
    if (!isAuthenticated) {
      return (
        <div className="flex-1 flex items-center justify-center p-4 h-full bg-card">
          <div className="text-center max-w-md">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
              <UserIcon className="size-6 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold mb-2">Connect to Continue</h2>
            <p className="text-muted-foreground text-sm mb-4">
              Connect to view your account details.
            </p>
            <Button onClick={() => setLoginModalOpen(true)}>
              Connect
            </Button>
            <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
          </div>
        </div>
      );
    }

    return (
      <div className="h-full overflow-auto bg-card">
        <div className="p-3 space-y-3">
          {/* Header */}
          <div className="pb-1">
            <h1 className="text-lg font-bold">Account Overview</h1>
            <p className="text-muted-foreground text-xs">
              Track your trading performance
            </p>
          </div>

          {/* Account cards - optimized for mobile */}
          <PortfolioValueCard />
          <AccountHealthCard />
          <PositionDistributionCard />
          <TradingStatsCard />
          <RecentActivityCard />
        </div>
      </div>
    );
  }

  // Desktop: show original margin info panel
  return (
    <TradingPanelCard className="!h-[175px] overflow-auto" data-tour="account-panel">
      <MarginInfo />
    </TradingPanelCard>
  );
};
